import { Banner, Button, TextLink } from '@/components/DesignSystem';
import { AboutAccountRolesDialog } from '@/components/shared/AboutAccountRolesDialog';
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { ApiUser } from '@api/interfaces';
import { useTheme } from "@mui/material";
import { AccountGroupRole, CIBPageName } from '@shared/enums';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from "react-material-symbols";
import { recordBannerDismissal, shouldShowBannerByDismissalRules } from './bannerDismissalUtils';
import { getBannerContent } from './ContextualBannerContent';

interface AboutAccountRolesBannerProps {
  brandColor?: string,
  customerRole: AccountGroupRole,
  linkedAccounts: ApiUser[],
  pageName: CIBPageName,
  showMobileVersion?: boolean,
}

export const AboutAccountRolesBanner: React.FC<AboutAccountRolesBannerProps> = ({
  brandColor,
  customerRole,
  linkedAccounts,
  pageName,
  showMobileVersion,
}: AboutAccountRolesBannerProps) => {
  const [infoBannerDismissed, setInfoBannerDismissed] = useState(false);
  const { isMobile } = useMediaQuery();
  const [showAboutAccountRolesDialog, setShowAboutAccountRolesDialog] = useState(false);
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const shouldShowMobileVersion = showMobileVersion ?? isMobile;

  const isRetailerPortal = pageName === CIBPageName.RP_CurrentProgress ||
    pageName === CIBPageName.RP_CustomerRecord ||
    pageName === CIBPageName.RP_PointsActivity ||
    pageName === CIBPageName.RP_Wallet;

  const bannerContent = useMemo(() => {
    return getBannerContent(customerRole, linkedAccounts, pageName, i18n.language);
  }, [customerRole, i18n.language, linkedAccounts, pageName]);

  const showInfoBanner = useMemo(() => {
    if (!shouldShowBannerByDismissalRules()) {
      return false;
    }
    return !infoBannerDismissed;
  }, [infoBannerDismissed]);

  const handleBannerClose = () => {
    setInfoBannerDismissed(true);
    recordBannerDismissal();
  };

  if (!showInfoBanner || !bannerContent) {
    return null;
  }

  return (
    <>
      <Banner
        actionButtons={!shouldShowMobileVersion ? [
          <Button
            key="learn-more-button"
            onClick={() => setShowAboutAccountRolesDialog(true)}
            testID="learn-more-button"
          >
            {t($ => $.common.actions.learnMore)}
          </Button>,
        ] : [
          <TextLink
            category="label-medium"
            key="about-account-roles-button"
            onClick={() => setShowAboutAccountRolesDialog(true)}
            sx={{ cursor: 'pointer', color: theme.palette.info.dark }}
            testID="banner-action-text-button"
          >
            {t($ => $.customerDashboard.howItWorks.buttons.aboutAccountRoles)}
          </TextLink>,
        ]}
        icon={!shouldShowMobileVersion ? (
          <MaterialSymbol
            fill
            icon="groups"
          />
        ) : null}
        messages={bannerContent.messages}
        onClose={handleBannerClose}
        showMobileVersion={shouldShowMobileVersion}
        status="info"
        sx={{
          color: 'text.primary',
          ...(pageName === CIBPageName.CRD_LandingPage && {
            borderRadius: 0,
          }),
        }}
        title={bannerContent.title}
      />
      {showAboutAccountRolesDialog && (
        <AboutAccountRolesDialog
          brandColor={brandColor}
          customerRole={customerRole as AccountGroupRole}
          linkedAccounts={linkedAccounts}
          onClose={() => setShowAboutAccountRolesDialog(false)}
          pageType={isRetailerPortal ? 'rp' : 'crd'}
        />
      )}
    </>
  );
};
